// src/pages/SearchRoom.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';
import CenterLayout from '../components/CenterLayout';

export default function SearchRoom({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // URL 파라미터 초기값
  const initRoomId   = searchParams.get('roomId')    || '';
  const initPw       = searchParams.get('pw')        || '';
  const initDay      = searchParams.get('day')       || '';
  const initDept     = searchParams.get('department')|| '';
  const initYear     = searchParams.get('year')      || '';
  const initPeriod   = searchParams.get('period')    || '';
  const initSubject  = searchParams.get('subject')   || '';

  // 폼 상태
  const [day, setDay]               = useState(initDay);
  const [department, setDepartment] = useState(initDept);
  const [year, setYear]             = useState(initYear);
  const [period, setPeriod]         = useState(initPeriod);
  const [subject, setSubject]       = useState(initSubject);
  const [password, setPassword]     = useState(initPw);
  const [rooms, setRooms]           = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 검색 함수
  const doSearch = async () => {
    setIsSearching(true);
    setHasSearched(true);
    
    const clauses = [];
    if (day)        clauses.push(where('day', '==', day));
    if (department) clauses.push(where('department', '==', department));
    if (year)       clauses.push(where('year', '==', year));
    if (period)     clauses.push(where('period', '==', Number(period)));
    if (subject)    clauses.push(where('subject', '==', subject));

    const q = clauses.length
      ? query(collection(db, 'rooms'), ...clauses)
      : collection(db, 'rooms');

    try {
      const snap = await getDocs(q);
      setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('방 검색 중 오류:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // 1) 마운트 시, URL 파라미터가 있으면 폼 채우고 즉시 검색
  useEffect(() => {
    if (initRoomId) {
      doSearch();
    }
  }, []); // mount only

  // 2) 수동 검색 트리거
  const handleSearchClick = () => {
    doSearch();
  };

  // 3) 입장
  const handleJoin = roomId => {
    navigate(`/rooms/${roomId}?pw=${encodeURIComponent(password)}`);
  };

  // 폼 리셋
  const resetForm = () => {
    setDay('');
    setDepartment('');
    setYear('');
    setPeriod('');
    setSubject('');
    setPassword('');
  };

  return (
    <CenterLayout>
      {/* 헤더 */}
      <div style={{
        marginBottom: '1.5rem',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        background: 'white',
        padding: isMobile ? '1.2rem 1.5rem' : '1.5rem 2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        border: '1px solid #f4f4f8'
      }}>
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          width: '6px',
          height: '100%',
          background: '#4f46e5'
        }}></div>
        <h2 style={{
          margin: 0,
          marginLeft: '6px',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          fontWeight: 600,
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '36px' : '42px',
            height: isMobile ? '36px' : '42px',
            borderRadius: '12px',
            background: '#f3f4ff',
            color: '#4f46e5'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          방 검색
        </h2>
      </div>
      
      {/* 검색 폼 */}
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: '1px solid #f1f5f9',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          {/* 요일 선택 */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>요일</label>
            <select 
              value={day} 
              onChange={e => setDay(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
              }}
            >
              <option value="">요일 선택</option>
              <option>월요일</option>
              <option>화요일</option>
              <option>수요일</option>
              <option>목요일</option>
              <option>금요일</option>
            </select>
          </div>

          {/* 학과 선택 */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>학과</label>
            <select 
              value={department} 
              onChange={e => setDepartment(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
              }}
            >
              <option value="">학과 선택</option>
              <option>소프트웨어학과</option>
              <option>컴퓨터공학과</option>
              <option>디자인컨버전스</option>
              <option>신소재공학과</option>
            </select>
          </div>

          {/* 학년 선택 */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>학년</label>
            <select 
              value={year} 
              onChange={e => setYear(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
              }}
            >
              <option value="">학년 선택</option>
              <option>1학년</option>
              <option>2학년</option>
              <option>3학년</option>
              <option>4학년</option>
            </select>
          </div>

          {/* 교시 선택 */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>교시</label>
            <select 
              value={period} 
              onChange={e => setPeriod(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem',
                paddingRight: '2.5rem',
              }}
            >
              <option value="">교시 선택</option>
              {[...Array(9)].map((_, i) => (
                <option key={i} value={i+1}>{i+1}교시</option>
              ))}
            </select>
          </div>
        </div>

        {/* 과목명 & 비밀번호 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>과목명</label>
            <input
              placeholder="과목명 입력"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: '#64748b',
            }}>비밀번호 (선택)</label>
            <input
              type="password"
              placeholder="비밀번호 입력"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
              }}
            />
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          <button 
            onClick={resetForm}
            style={{
              padding: isMobile ? '0.8rem' : '0.75rem',
              borderRadius: '0.5rem',
              border: '1px solid #cbd5e1',
              background: 'transparent',
              color: '#64748b',
              fontWeight: '500',
              fontSize: '0.95rem',
              cursor: 'pointer',
              flex: isMobile ? '1 0 100%' : 'initial',
              order: isMobile ? 2 : 1,
              marginBottom: isMobile ? '0' : '0',
            }}
          >
            초기화
          </button>
          <button 
            onClick={handleSearchClick}
            disabled={isSearching}
            style={{
              padding: isMobile ? '0.8rem' : '0.75rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: '#6366f1',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.95rem',
              cursor: isSearching ? 'not-allowed' : 'pointer',
              flex: isMobile ? '1 0 100%' : 1,
              order: isMobile ? 1 : 2,
              marginBottom: isMobile ? '0.5rem' : '0',
              opacity: isSearching ? 0.7 : 1,
            }}
          >
            {isSearching ? '검색 중...' : '검색하기'}
          </button>
        </div>
      </div>

      {/* 검색 결과 */}
      <div style={{
        marginBottom: isMobile ? '5rem' : '2rem',
      }}>
        {hasSearched && (
          <div style={{ 
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#1e293b',
            }}>
              검색 결과 ({rooms.length}개)
            </h3>
          </div>
        )}

        {hasSearched && rooms.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2.5rem 1.5rem',
            textAlign: 'center',
            border: '1px solid #f1f5f9',
            boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
          }}>
            <div style={{
              marginBottom: '1rem',
              color: '#94a3b8',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>
            <p style={{
              margin: '0',
              fontSize: '1.1rem',
              fontWeight: 500,
              color: '#64748b',
            }}>검색 결과가 없습니다</p>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.95rem',
              color: '#94a3b8',
            }}>다른 검색 조건으로 다시 시도해보세요</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}>
            {rooms.map(r => (
              <div 
                key={r.id} 
                style={{
                  background: r.password ? 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)' : 'white',
                  borderRadius: '0.75rem',
                  padding: '1.25rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: r.password ? '1px solid #fbbf24' : '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem',
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    maxWidth: '85%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {r.subject}
                  </h3>
                  {r.password && (
                    <div style={{
                      flexShrink: 0,
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(251, 191, 36, 0.15)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="8" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    </div>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  marginBottom: '1rem',
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    padding: '0.35rem 0.7rem',
                    borderRadius: '1rem',
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    fontWeight: '500',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                  }}>
                    {r.day} {r.period}교시
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#64748b',
                  }}>
                    {r.department || '-'}
                  </div>
                </div>
                
                <div style={{
                  marginTop: 'auto', 
                  paddingTop: '0.75rem',
                  borderTop: '1px solid #f1f5f9',
                }}>
                  <button
                    onClick={() => handleJoin(r.id)}
                    style={{
                      width: '100%',
                      padding: isMobile ? '0.75rem 0' : '0.65rem 0',
                      background: r.password ? 'transparent' : 'rgba(99, 102, 241, 0.05)',
                      border: r.password 
                        ? '1px solid #f59e42'
                        : '1px solid #6366f1',
                      color: r.password ? '#f59e42' : '#6366f1',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                    }}
                  >
                    {r.password ? '비밀번호 입력' : '입장하기'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CenterLayout>
  );
}
